import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import argusLogo from '../../assets/images/ChatGPT_Image_Dec_6_2025_from_Product_Suggestions-1765015257703.png';
import Select from './Select';
import { useSidebar } from '../../hooks/useSidebar';

export default function Sidebar() {
  const [activeModules] = useState(['all']);
  const [userRole] = useState('Safety Manager');
  const [expandedItems, setExpandedItems] = useState({});

  const menuItems = [
    { icon: 'Home', label: 'Dashboard', path: '/module-selection-dashboard' },
    { icon: 'Tower', label: 'Safety Control Tower', path: '/safety-control-tower' },
    { icon: 'Activity', label: 'Analytics', path: '/safety-analytics-dashboard' },
    { icon: 'FileText', label: 'Work Permits', path: '/work-permit-tracking' },
    { icon: 'AlertTriangle', label: 'Incident Management', path: '/incident-reporting-dashboard' },
    { icon: 'BarChart3', label: 'Incident Investigation', path: '/incident-investigation-tracker' },
    {
      icon: 'Database',
      label: "PSI Repository",
      path: "/process-safety-information-repository",
      children: [
        { icon: 'Flask', label: 'Chemical Info', path: '/chemical-information-manager' },
        { icon: 'Grid', label: 'Compatibility Matrix', path: '/chemical-compatibility-matrix-manager' },
        { icon: 'Database', label: 'Database Hub', path: '/material-database-integration-hub' },
      ],
    },
    { icon: 'AlertCircle', label: 'Risk Identification', path: '/risk-identification' },
    { 
      icon: 'Eye', 
      label: 'Unsafe Acts & Conditions', 
      path: '/unsafe-acts-conditions-analytics-dashboard',
      children: [
        { icon: 'BarChart3', label: 'Analytics Dashboard', path: '/unsafe-acts-conditions-analytics-dashboard' },
        { icon: 'FileEdit', label: 'Report Entry', path: '/unsafe-acts-conditions-entry-form' },
        { icon: 'ClipboardCheck', label: 'Task Management', path: '/task-management-approval-center' },
        { icon: 'Video', label: 'AI Vision Center', path: '/ai-vision-integration-control-center' },
      ],
    },
    { icon: 'ShieldCheck', label: 'Safety Audits', path: '/safety-audit-dashboard' },
    {
      icon: 'GraduationCap',
      label: 'Training and Compliance',
      path: '/training-compliance-dashboard',
      children: [
        { icon: 'BarChart3', label: 'Training Compliance Dashboard', path: '/training-compliance-dashboard' },
        { icon: 'BookOpen', label: 'Training Materials Repository', path: '/training-materials-repository' },
        { icon: 'ClipboardCheck', label: 'Training Assessment Centre', path: '/training-assessment-center' },
      ],
    },
    {
      icon: 'Scale',
      label: 'Statutory Compliance',
      path: '/statutory-compliance-analytics-dashboard',
      children: [
        { icon: 'BarChart3', label: 'Analytics Dashboard', path: '/statutory-compliance-analytics-dashboard' },
        { icon: 'Calendar', label: 'Task Manager', path: '/compliance-task-manager' },
        { icon: 'FileText', label: 'Document Repository', path: '/compliance-document-repository' },
        { icon: 'Clock', label: 'Timeline & Validation', path: '/statutory-compliance-timeline-validation-center' },
      ],
    },
    {
      icon: 'FileBarChart',
      label: 'Reports',
      path: '/reports-management-dashboard',
      children: [
        { icon: 'LayoutGrid', label: 'Reports Dashboard', path: '/reports-management-dashboard' },
        { icon: 'FileEdit', label: 'Custom Report Builder', path: '/custom-report-builder' },
        { icon: 'Sparkles', label: 'AI Report Assistant', path: '/ai-report-assistant' },
      ],
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const { isCollapsed, isMobileOpen, toggleCollapse, toggleMobile, closeMobile } = useSidebar();

  const [selectedLocation, setSelectedLocation] = useState('plant-1');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userName = currentUser.name || 'User';
  // const userRole = currentUser.role || 'Safety Manager';

  const handleLogout = () => {
    localStorage.removeItem('currentUser');
    navigate('/login');
  };

  const handleLogoutClick = () => {
    setShowUserMenu(false);
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    handleLogout();
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const locationOptions = [
    { value: 'plant-1', label: 'Plant 1 - Main Facility' },
    { value: 'plant-2', label: 'Plant 2 - North Wing' },
    { value: 'plant-3', label: 'Plant 3 - South Wing' },
    { value: 'warehouse-1', label: 'Warehouse 1' },
  ];

  const toggleExpand = (label, e) => {
    e?.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [label]: !prev?.[label]
    }));
  };

  const isActive = (path) => location?.pathname === path;

  const hasModuleAccess = (moduleRequired) => {
    if (!moduleRequired) return true;
    return activeModules?.includes(moduleRequired);
  };

  const handleNavigation = (path) => {
    if (path) {
      navigate(path);
      closeMobile();
    }
  };

  const renderNavItem = (item, isChild = false) => {
    if (!hasModuleAccess(item?.moduleRequired)) return null;

    const active = isActive(item?.path);
    const hasChildren = item?.children && item?.children?.length > 0;
    const isExpanded = expandedItems?.[item?.label] || false;

    if (hasChildren) {
      return (
        <div key={item?.label} className="space-y-1">
          <div className="flex items-center">
            <div
              className={`sidebar-nav-item ${active ? 'active' : ''} ${isCollapsed ? 'justify-center' : 'flex-1'} cursor-pointer`}
              onClick={() => !isCollapsed && handleNavigation(item?.path)}
              title={isCollapsed ? item?.tooltip || item?.label : ''}
            >
              <Icon name={item?.icon} size={20} />
              {!isCollapsed && (
                <>
                  <span className="sidebar-nav-item-text flex-1">{item?.label}</span>
                  {item?.moduleRequired && (
                    <span className={`module-badge ${hasModuleAccess(item?.moduleRequired) ? '' : 'inactive'}`} />
                  )}
                </>
              )}
            </div>
            {!isCollapsed && (
              <button
                onClick={(e) => toggleExpand(item?.label, e)}
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent/10 transition-colors ml-1"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={18} />
              </button>
            )}
          </div>
          {!isCollapsed && isExpanded && (
            <div className="ml-8 space-y-1">
              {item?.children?.map((child) => renderNavItem(child, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={item?.label}
        className={`sidebar-nav-item ${active ? 'active' : ''} ${isChild ? 'text-xs' : ''} ${isCollapsed ? 'justify-center' : ''}`}
        onClick={() => handleNavigation(item?.path)}
        title={isCollapsed ? item?.tooltip || item?.label : ''}
      >
        <Icon name={item?.icon} size={isChild ? 16 : 20} />
        {!isCollapsed && (
          <>
            <span className="sidebar-nav-item-text flex-1">{item?.label}</span>
            {item?.moduleRequired && (
              <span className={`module-badge ${hasModuleAccess(item?.moduleRequired) ? '' : 'inactive'}`} />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <button
        className="mobile-menu-button"
        onClick={closeMobile}
        aria-label="Toggle mobile menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      {isMobileOpen && (
        <div
          className="mobile-overlay"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}
      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <img 
                src={argusLogo} 
                alt="RUBICR Argus logo with circular emblem featuring geometric triangular elements" 
                className="w-8 h-8 object-contain"
              />
            </div>
            <span className="sidebar-logo-text">Argus</span>
          </div>
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-lg hover:bg-accent/10 transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
          </button>
        </div>

        {!isCollapsed && (
          <div className="location-selector">
            <Select
              options={locationOptions}
              value={selectedLocation}
              onChange={setSelectedLocation}
              placeholder="Select location"
            />
          </div>
        )}

        <nav className="sidebar-nav flex-1 overflow-y-auto">
          {menuItems?.map((item) => renderNavItem(item))}
        </nav>

        <div className="sidebar-footer">
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/10 rounded-lg transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground text-sm font-medium">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
              {!isCollapsed && (
                <>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground">{userName}</p>
                    <p className="text-xs text-muted-foreground">{userRole}</p>
                  </div>
                  <Icon name="ChevronUp" size={16} color="#94A3B8" className={`transition-transform ${showUserMenu ? '' : 'rotate-180'}`} />
                </>
              )}
            </button>
          </div>
        </div>
      </aside>
      {showUserMenu && (
        <div className={`${isCollapsed ? 'fixed bottom-4 left-20' : 'fixed bottom-20 left-60'} bg-card border border-border rounded-lg shadow-lg overflow-hidden z-[9999]`}>
          <button
            onClick={() => { setShowUserMenu(false); navigate('/#'); }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/10 transition-colors text-left whitespace-nowrap"
          >
            <Icon name="Settings" size={16} color="#0F172A" />
            <span className="text-sm text-foreground">Settings</span>
          </button>
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-destructive/10 transition-colors text-left border-t border-border whitespace-nowrap"
          >
            <Icon name="LogOut" size={16} color="#DC2626" />
            <span className="text-sm text-destructive">Logout</span>
          </button>
        </div>
      )}
      {showLogoutModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-[9998]" onClick={cancelLogout} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl p-6 z-[9999] w-[90%] max-w-md">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <Icon name="LogOut" size={24} color="#DC2626" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2">Confirm Logout</h3>
                <p className="text-sm text-muted-foreground">Are you sure you want to logout? You will need to login again to access the application.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 transition-colors font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export { useSidebar };